import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { MaskLines, Reveal } from "../components/Motion";
import { useFetch } from "../lib/useFetch";
import { money, startCheckout, errorText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const QUIZ = [
  { k: "role", q: "Which best describes you?", o: [["student", "Student or researcher"], ["pro", "Executive or professional"], ["org", "Library, school or company"]] },
  { k: "format", q: "How do you like to read?", o: [["digital", "On screen, anywhere"], ["print", "Print on my desk, digital on the go"]] },
  { k: "cadence", q: "How often will you read BPJ?", o: [["often", "Every week"], ["sometimes", "A few times a month"]] },
];

function recommend(a) {
  if (a.role === "org") return "institutional_annual";
  if (a.format === "print") return "allaccess_annual";
  if (a.role === "student") return "scholar_annual";
  return a.cadence === "often" ? "digital_monthly" : "digital_monthly";
}

function Quiz({ onDone }) {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const pick = (v) => {
    const next = { ...ans, [QUIZ[step].k]: v };
    setAns(next);
    if (step < QUIZ.length - 1) setStep(step + 1); else onDone(recommend(next));
  };
  const item = QUIZ[step];
  return (
    <div className="border border-[var(--ink)] p-6 sm:p-10 bg-white" data-testid="plan-quiz">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Plan finder · {step + 1}/{QUIZ.length}</p>
        <div className="flex gap-1">{QUIZ.map((_, i) => <span key={i} className={`h-1 w-8 ${i <= step ? "bg-[var(--red)]" : "bg-[var(--line)]"}`} />)}</div>
      </div>
      <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45 }}>
        <h3 className="f-serif text-3xl sm:text-4xl mt-8">{item.q}</h3>
        <div className="mt-8 grid gap-3">
          {item.o.map(([v, l]) => (
            <button key={v} onClick={() => pick(v)} data-testid={`quiz-option-${v}`} className="group text-left border rule px-5 py-4 flex items-center justify-between hover:border-[var(--ink)] hover:bg-[var(--paper)] transition-colors">
              <span className="text-lg">{l}</span><ArrowRight size={18} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function Subscribe() {
  const { data } = useFetch("/catalog", { plans: [], products: [] });
  const plans = Array.isArray(data?.plans) ? data.plans : [];
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const buy = async (id) => {
    if (!user) { toast("Create a free account or sign in first — your subscription will be linked to it."); nav("/account?next=/subscribe"); return; }
    setBusy(id);
    try { await startCheckout([{ lookup_key: id, quantity: 1 }]); }
    catch (e) { toast.error(errorText(e)); setBusy(null); }
  };
  return (
    <div className="px-5 lg:px-12 pt-14" data-testid="subscribe-page">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <p className="eyebrow mb-6">Subscriptions</p>
          <h1 className="f-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]"><MaskLines lines={["A plan shaped", <em key="a" className="font-light">around you.</em>]} /></h1>
          <p className="text-lg text-[var(--ink-2)] mt-8 max-w-md leading-relaxed">Answer three questions and we'll recommend the subscription that fits how you read — or compare every plan below.</p>
        </div>
        <div className="lg:col-span-6">
          {rec ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-[var(--ink)] p-6 sm:p-10 bg-[var(--ink)] text-white" data-testid="quiz-result">
              <p className="eyebrow !text-[#BDBDBD]">We recommend</p>
              <p className="f-serif text-5xl mt-4" data-testid="quiz-result-plan">{plans.find((p) => p.id === rec)?.name}</p>
              <p className="text-[#DADADA] mt-3">{plans.find((p) => p.id === rec)?.tagline}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => buy(rec)} className="btn-red h-12 px-6 font-semibold" data-testid="quiz-result-subscribe-btn">{busy === rec ? "Redirecting…" : "Subscribe now"}</button>
                <button onClick={() => setRec(null)} className="h-12 px-5 border border-white/30 flex items-center gap-2 hover:border-white transition-colors" data-testid="quiz-restart-btn"><RotateCcw size={16} /> Start over</button>
              </div>
            </motion.div>
          ) : <Quiz onDone={setRec} />}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border rule mt-24">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.06} className={`p-7 flex flex-col ${rec === p.id ? "bg-white outline outline-2 outline-[var(--red)] -outline-offset-2" : "bg-[var(--paper)]"}`}>
            <div data-testid={`plan-${p.id}`} className="flex flex-col h-full">
              <div className="flex items-center justify-between"><p className="eyebrow">{p.interval === "month" ? "Monthly" : "Annual"}</p>{rec === p.id && <span className="eyebrow !text-[var(--red)]">For you</span>}</div>
              <h3 className="f-serif text-3xl mt-4">{p.name}</h3>
              <p className="mt-2 text-[var(--muted)] min-h-[3rem]">{p.tagline}</p>
              <p className="mt-6"><span className="f-serif text-5xl">{money(p.price)}</span><span className="text-[var(--muted)]"> / {p.interval}</span></p>
              <ul className="mt-6 space-y-2.5 text-[0.95rem] flex-1">
                {(p.features || []).map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-[var(--red)] mt-1 shrink-0" />{f}</li>)}
              </ul>
              <button onClick={() => buy(p.id)} className={`${rec === p.id ? "btn-red" : "btn-ink"} mt-8 h-11 font-semibold`} data-testid={`subscribe-${p.id}`}>{busy === p.id ? "Redirecting…" : `Choose ${p.name}`}</button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
